// frontend/src/pages/CodeReview.tsx
// Premium özellik — Gemini ile detaylı GitHub Copilot tarzı kod incelemesi.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeatures } from '../hooks/useFeatures';
import { usePyodide } from '../hooks/usePyodide';
import { CodeEditorPanel } from '../components/code-editor/CodeEditorPanel';
import { geminiService, GeminiApiError } from '../services/gemini.service';
import '../styles/code-editor.css';

interface ReviewResult {
  codeQuality: number;        // 0-100
  strengths: string[];
  improvements: string[];
  suggestions: string[];      // satır bazlı öneriler
  overallScore: number;       // 0-100
  summary: string;
}

export function CodeReviewPage() {
  const { hasFeature } = useFeatures();
  const isPremium = hasFeature('ai.code_review');
  const { isLoading: pyodideLoading, runPython, isReady } = usePyodide();
  const [code, setCode] = useState('# Kodunu buraya yapıştır veya yaz\n');
  const [runOutput, setRunOutput] = useState<string | null>(null);
  const [review, setReview] = useState<ReviewResult | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleRun() {
    if (!isReady) return;
    const r = await runPython(code);
    setRunOutput(r.output || r.error || '(çıktı yok)');
  }

  async function handleReview() {
    setIsReviewing(true);
    setError(null);
    setReview(null);
    try {
      // evaluateCode ile detaylı analiz (zaten varolan servis)
      const basic = await geminiService.evaluateCode(code);

      // Ek olarak satır bazlı öneriler için ayrı bir çağrı
      const suggestionsRes = await geminiService.evaluateCodeDetailed(code, runOutput, 'python');

      setReview({
        codeQuality: basic.score,
        strengths: basic.strengths,
        improvements: basic.improvements,
        suggestions: suggestionsRes.improvements,
        overallScore: Math.round((basic.score + suggestionsRes.quality * 10 + suggestionsRes.logic * 10 + suggestionsRes.readability * 10) / 4),
        summary: basic.feedback,
      });
    } catch (err) {
      setError(err instanceof GeminiApiError ? err.message : 'Kod incelemesi başarısız.');
    } finally {
      setIsReviewing(false);
    }
  }

  if (!isPremium) {
    return (
      <div className="pricing-page">
        <div className="card admin-analytics-placeholder" style={{ padding: 64 }}>
          <div className="career-empty-icon">🔒</div>
          <h3>Bu özellik Premium'a özeldir</h3>
          <p className="parent-empty-text">
            AI Kod İncelemesi, GitHub Copilot tarzı detaylı analiz sunar. Erişmek için Premium plana geç.
          </p>
          <Link to="/pricing" className="lesson-btn primary">💎 Premium'a Geç</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="code-editor-page">
      <header className="code-editor-header">
        <h1>AI Kod İncelemesi 🔍</h1>
        <p>GitHub Copilot tarzı detaylı kod analizi — Premium özellik</p>
      </header>

      <div className="grid grid-2 code-editor-grid">
        <div>
          <CodeEditorPanel code={code} onChange={setCode} onRun={handleRun} onReset={() => { setCode(''); setRunOutput(null); setReview(null); }} isRunning={pyodideLoading} />
          {runOutput && (
            <div className="card output-panel-card" style={{ marginTop: 16 }}>
              <pre className="output-panel-stdout">{runOutput}</pre>
            </div>
          )}
        </div>

        <div>
          <div className="evaluation-card">
            <div className="evaluation-header">
              <h3>Kod İncelemesi</h3>
              <button type="button" className="lesson-btn primary sm" onClick={handleReview} disabled={isReviewing || !code.trim()}>
                {isReviewing ? '⏳ İnceleniyor...' : '🔍 İncele'}
              </button>
            </div>

            {error && <p className="evaluation-error">{error}</p>}

            {!review && !error && (
              <div className="evaluation-placeholder">
                <div className="evaluation-placeholder-icon">🔍</div>
                <p>Kodunu yaz ve "İncele" butonuna bas</p>
              </div>
            )}

            {review && (
              <div>
                {/* Genel Skor */}
                <div className="evaluation-scores">
                  <div className="evaluation-score">
                    <div className="evaluation-score-value" style={{ fontSize: 36, color: review.overallScore >= 70 ? 'var(--secondary)' : review.overallScore >= 40 ? 'var(--accent)' : 'var(--danger)' }}>
                      {review.overallScore}
                    </div>
                    <div className="evaluation-score-label">Genel Skor</div>
                  </div>
                  <div className="evaluation-score">
                    <div className="evaluation-score-value">{review.codeQuality}</div>
                    <div className="evaluation-score-label">Kod Kalitesi</div>
                  </div>
                </div>

                {review.summary && <p className="evaluation-overall-feedback">{review.summary}</p>}

                {/* Güçlü Yanlar */}
                {review.strengths.length > 0 && (
                  <div className="evaluation-feedback" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)', marginBottom: 12 }}>
                    <h4 style={{ color: 'var(--secondary)' }}>✅ Güçlü Yanlar</h4>
                    <ul>
                      {review.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}

                {/* Geliştirilecek Alanlar */}
                {review.improvements.length > 0 && (
                  <div className="evaluation-feedback" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', marginBottom: 12 }}>
                    <h4 style={{ color: 'var(--danger)' }}>⚠️ Geliştirilecek Alanlar</h4>
                    <ul>
                      {review.improvements.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}

                {/* Satır Önerileri */}
                {review.suggestions.length > 0 && (
                  <div className="evaluation-feedback">
                    <h4>💡 AI Önerileri</h4>
                    <ul>
                      {review.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
